import video_call_icon from 'src/assets/video_call_icon.png';
import close_icon_purple from 'src/assets/close_icon_purple.png';

import { Popover } from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JournalContext } from 'src/consumer/context/JournalContext'
import { LocalizationContext } from 'src/consumer/context/LocalizationContext'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import { hasCompletedConsumerOnboarding } from 'src/consumer/helpers/onboardingStatus'
import {
  canEventBeJoined,
  checkIfAllowedToJoinAllDay,
  checkIfEventUpcoming,
} from '../MyLiveSessions/my-live-sessions-utils'
import {
  BrandLogoWithLocation,
  GlobalNavProfileDropdown,
  NextSessionStrip,
  PageSelectDropdown,
  SlotNotificationPage,
} from './GlobalNavComponents'
import {
  BookedSlotsDrawer,
  GlobalNavCenter,
  GlobalNavContainer,
  GlobalNavLeft,
  GlobalNavParent,
  GlobalNavRight,
  JournalIcon,
  OneOnOneCountForIcon,
  SessionIcon,
  TrialBox,
} from './GlobalNavStyles'
import NotificationsList from '../Notifications/NotificationsList'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { showRedirectStrip } from '../ConsumerHomepage'
import PortalSwitcher from './PortalSwitcher'

const GlobalNav = ({
  tabOrder,
  tabsListMap,
  activeTile,
  unSecureSetupData,
  isMobile,
  openPrewallFunction,
  bookedSlotsDataPageOne,
  userScoreData,
  updateUserScoreData,
}) => {
  const navigate = useNavigate()
  const { journalData, setJournalData } = useContext(JournalContext)
  const { profileData } = useContext(ProfileContext)
  const { setupData } = useContext(SetupContext)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  } // keeping treack of width
  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth)
    // Clean up the event listener on component unmount
    return () => window.removeEventListener('resize', updateWindowWidth)
  }, []) // Empty dependency array ensures the event listener is only added once

  const [anchorElBookedSlots, setAnchorElBookedSlots] = useState()
  const handleBookedSlotPopoverClosed = () => {
    setAnchorElBookedSlots(null)
  }
  const handleOpenBookedSlots = (event) => {
    setAnchorElBookedSlots(event.currentTarget)
  }
  const bookedSlotsOpen = Boolean(anchorElBookedSlots)

  const [currentTime, setCurrentTime] = useState(dayjs().format('YYYY-MM-DD HH:mm:ss'))
  const [areAllStripsVisible, setAreAllStripsVisible] = useState(false)

  const [bookedSlotsDrawerOpen, setBookedSlotsDrawerOpen] = useState(false)

  const pageId = 'tilePage'

  const { localizedDataLoading, localizedData, localizedDataError } =
    useContext(LocalizationContext)
  const localizedStrings = localizedData?.[pageId]

  let pages = []
  // ROLE_PATIENT no longer contributes a 'patient-home' entry to the wellness
  // page-switcher dropdown — patients use the dedicated /patient-home/* portal.
  if (profileData && hasCompletedConsumerOnboarding(profileData)) {
    const getTabs = (tabOrder, tabsListMap) => {
      tabOrder?.tabs?.forEach((tab) => {
        if (tabsListMap.get(tab)?.curatable) {
          pages.push({ value: tab, label: tabsListMap.get(tab)?.title })
        }
      })
      tabOrder?.tiles?.forEach((tile) => {
        if (tabsListMap.get(tile)?.curatable) {
          pages.push({ value: tile, label: tabsListMap.get(tile)?.title })
        }
      })
    }
    getTabs(tabOrder, tabsListMap)
  } else {
    pages.push({ value: 'explore-wellness-app', label: 'Explore Wellness App' })
  }
  const getActivePage = (pages) => {
    return pages?.find((page) => {
      if (page?.value === activeTile) {
        return page
      }
    })
  }
  const activePage = getActivePage(pages)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(dayjs().format('YYYY-MM-DD HH:mm:ss'))
    }, 30000)

    return () => clearInterval(interval)
  }, []) //setting time every minute

  const isFreeUser = profileData?.subscriptionTypeId === 'subscription_free'
  const isTrialSubscription = profileData?.subscriptionPlanType === 'freeTrial'
  const trialExpiryDays =
    (profileData?.subscriptionEndDate - profileData?.subscriptionStartDate) / (60 * 60 * 24)
  let trialBanner = ''
  trialBanner = isFreeUser
    ? profileData?.trialOffered
      ? 'Trial Expired'
      : ''
    : isTrialSubscription
    ? `Trial expires in ${trialExpiryDays} days`
    : ''
  let meetingPlatformImage = video_call_icon

  // UPCOMING SESSIONS STRIP(s):

  const eventsNotYetEndedList = bookedSlotsDataPageOne?.filter(
    (item) => checkIfEventUpcoming(item, currentTime) || checkIfAllowedToJoinAllDay(item),
  ) // slots with end time after current time

  eventsNotYetEndedList.reverse()
  let nextAvailableSlot
  if (eventsNotYetEndedList && eventsNotYetEndedList?.length > 0) {
    nextAvailableSlot = eventsNotYetEndedList?.[0]
  }

  return (
    <GlobalNavContainer isMobile={isMobile} leavePaddingTop= {showRedirectStrip(window.location.pathname)} dark={true}>
      <GlobalNavParent>
        <GlobalNavLeft>
          <BrandLogoWithLocation darkText={false} />
          {trialBanner && (
            <TrialBox onClick={openPrewallFunction} darkText={false}>
              {trialBanner}
            </TrialBox>
          )}
        </GlobalNavLeft>

        <GlobalNavCenter>
          <PageSelectDropdown
            darkText={false}
            options={pages}
            placeholder="Explore"
            value={activePage}
            onChange={(selected) => {
              if (selected?.value === 'explore-wellness-app') {
                navigate('/onboarding')
              } else {
                navigate(`/tile/${selected?.value}`, {})
              }
            }}
            tileId={activeTile}
          />
        </GlobalNavCenter>

        <GlobalNavRight>
          <SessionIcon
            onClick={(event) => {
              if (windowWidth > 520) {
                handleOpenBookedSlots(event)
              } else {
                setBookedSlotsDrawerOpen(true)
              }
            }}
          >
            1-on-1
            {eventsNotYetEndedList?.length > 0 && (
              <OneOnOneCountForIcon>{eventsNotYetEndedList?.length}</OneOnOneCountForIcon>
            )}
          </SessionIcon>

          {windowWidth > 520 && (
            <Popover
              id="booked-slots-popover"
              open={bookedSlotsOpen}
              anchorEl={anchorElBookedSlots}
              onClose={handleBookedSlotPopoverClosed}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <SlotNotificationPage
                partialBookedSlotsData={eventsNotYetEndedList}
                withMaxHeight
                profileData={profileData}
              />
            </Popover>
          )}

          <JournalIcon
            onClick={() =>
              setJournalData({
                showJournal: true,
                callSource: 'navbar',
                data: { callSourceId: 'navbar', sourceName: 'navbar' },
                pageState: '',
                subscription: '',
              })
            }
          />

          <NotificationsList />

          <PortalSwitcher darkText={false} isMobile={isMobile} />

          <GlobalNavProfileDropdown
            unSecureSetupData={unSecureSetupData}
            localizedStrings={localizedStrings}
            setBookedSlotsDrawerOpen={setBookedSlotsDrawerOpen}
            windowWidth={windowWidth}
          />
        </GlobalNavRight>
      </GlobalNavParent>

      {nextAvailableSlot && canEventBeJoined(nextAvailableSlot, currentTime) && (
        <NextSessionStrip
          nextAvailableSlot={nextAvailableSlot}
          currentTime={currentTime}
          eventsNotYetEndedList={eventsNotYetEndedList}
          showShowAllStrip={eventsNotYetEndedList?.length > 1}
          areAllStripsVisible={areAllStripsVisible}
          toggleShowAllStrips={() => setAreAllStripsVisible((prev) => !prev)}
        />
      )}

      {areAllStripsVisible &&
        eventsNotYetEndedList?.length > 1 &&
        eventsNotYetEndedList
          ?.slice(1)
          ?.map((item, index) => (
            <NextSessionStrip
              key={index}
              nextAvailableSlot={item}
              currentTime={currentTime}
              eventsNotYetEndedList={eventsNotYetEndedList}
              showShowAllStrip={false}
              areAllStripsVisible={areAllStripsVisible}
              toggleShowAllStrips={() => setAreAllStripsVisible((prev) => !prev)}
            />
          ))}

      {bookedSlotsDrawerOpen && windowWidth < 520 && (
        <BookedSlotsDrawer
          className="drawer"
          anchor="bottom"
          open={bookedSlotsDrawerOpen}
          transitionDuration={{ enter: 600, exit: 300 }}
        >
          <img
            alt="close_icon"
            src={close_icon_purple}
            style={{
              cursor: 'pointer',
              height: '20px',
              position: 'absolute',
              top: '30px',
              right: '30px',
            }}
            onClick={() => setBookedSlotsDrawerOpen(false)}
          />
          <SlotNotificationPage
            partialBookedSlotsData={eventsNotYetEndedList}
            profileData={profileData}
          />
        </BookedSlotsDrawer>
      )}

      <SEOComponent
        title={activePage?.label || 'AUMHUM'}
        description={'Explore AUMHUM content library.'}
        image={APP_CONSTANTS.APP_LOGO_LINK}
        keywordsArr={[]}
      />
    </GlobalNavContainer>
  )
}

export default GlobalNav
